import { useTranslation } from "react-i18next";
import "../../../styles/mapStyles/Popup.css";
import {
  OFRA_APPLICATION_METHOD_OPTIONS,
  OFRA_FERTILIZER_TYPE_OPTIONS,
  OFRA_VOLUME_CONTAINER_OPTIONS,
} from "../../../constants/ofraCalculatorOptions";
import {
  calculateOfraFertilizerRate,
  formatOfraNumber,
} from "../../../utils/ofraFertilizerRateCalculation";
import {
  getContainerLabel,
  getFertilizerTypeLabel,
  getApplicationMethodLabel,
} from "../../../utils/ofraDisplayFormatter";

// ──────────────────────────────────────────────────────────
// OfraFertilizerRateCalculator
// 
// This component lets the farmer manually calculate how many
// points (or meters) of fertilizer one container can cover.
// 
// It now receives all state from its parent component so the 
// user's choices can be saved and later included in the PDF report.
// ──────────────────────────────────────────────────────────
function OfraFertilizerRateCalculator({
  containerValue,
  setContainerValue,
  fertilizerTypeValue,
  setFertilizerTypeValue,
  fertilizerRateValue,
  setFertilizerRateValue,
  applicationMethodValue,
  setApplicationMethodValue,
}) {

  const { t } = useTranslation();

  // Run the OFRA calculation with the current user inputs (from props)
  const calculation = calculateOfraFertilizerRate({
    containerValue,
    fertilizerTypeValue,
    fertilizerRateValue,
    applicationMethodValue,
  });

  // Check if the user has entered any values at all
  // If nothing is filled in, show an empty state message instead of errors
  const hasAnyInput =
    containerValue !== "" ||
    fertilizerTypeValue !== "" ||
    fertilizerRateValue !== "" ||
    applicationMethodValue !== "";

  // Show validation error message if something is wrong and the user has started typing
  const validationMessage =
    calculation.status === "invalid" && hasAnyInput
      ? t(`ofra.validation.${calculation.validationKey}`)
      : "";

  // Check if the calculation was successful
  const isValidCalculation = calculation.status === "valid";
  // Get the human-readable label for the result (e.g., "Points per Container" or "Meters per Container")
  const resultLabel = isValidCalculation ? t(calculation.resultLabelKey) : "";

  return (
    <section className="ofra-calculator-container" aria-labelledby="ofra-calculator-title">
      <strong id="ofra-calculator-title" className="results-title">
        {t("ofra.title")}
      </strong>

      <p className="ofra-calculator-intro">{t("ofra.intro")}</p>

      <div className="calculator-field">
        <label className="calculator-field-label">{t("ofra.volumeContainer")}</label>
        <select
          className="calculator-select"
          value={containerValue}
          onChange={(e) => setContainerValue(e.target.value)}
        >
          <option value="">{t("ofra.selectVolumeContainer")}</option>
          {OFRA_VOLUME_CONTAINER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {getContainerLabel(option.value)}
            </option>
          ))}
        </select>
      </div>

      <div className="calculator-field">
        <label className="calculator-field-label">{t("ofra.fertilizerType")}</label>
        <select
          className="calculator-select"
          value={fertilizerTypeValue}
          onChange={(e) => setFertilizerTypeValue(e.target.value)}
        >
          <option value="">{t("ofra.selectFertilizerType")}</option>
          {OFRA_FERTILIZER_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {getFertilizerTypeLabel(option.value)}
            </option>
          ))}
        </select>
      </div>

      <div className="calculator-field">
        <label className="calculator-field-label">{t("ofra.fertilizerRate")}</label>
        <input
          type="number"
          min="0"
          step="any"
          className="calculator-input"
          value={fertilizerRateValue}
          placeholder={t("ofra.enterFertilizerRate")}
          onChange={(e) => setFertilizerRateValue(e.target.value)}
        />
      </div>

      <div className="calculator-field">
        <label className="calculator-field-label">{t("ofra.applicationMethod")}</label>
        <select
          className="calculator-select"
          value={applicationMethodValue}
          onChange={(e) => setApplicationMethodValue(e.target.value)}
        >
          <option value="">{t("ofra.selectApplicationMethod")}</option>
          {OFRA_APPLICATION_METHOD_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {getApplicationMethodLabel(option.value)}
            </option>
          ))}
        </select>
      </div>

      {calculation.status === "valid" && (
        <div className="ofra-method-details" aria-live="polite">
          <div className="ofra-method-detail">
            <span className="ofra-method-detail-label">{t(calculation.spacingLabelKey)}</span>
            <span className="ofra-method-detail-value">
              {formatOfraNumber(calculation.spacingValue)} m
            </span>
          </div>
          <div className="ofra-method-detail">
            <span className="ofra-method-detail-label">{t(calculation.secondaryLabelKey)}</span>
            <span className="ofra-method-detail-value">
              {formatOfraNumber(calculation.secondaryValue)} m
            </span>
          </div>
        </div>
      )}

      {validationMessage && <div className="ofra-validation-message">{validationMessage}</div>}

      {!hasAnyInput && <div className="ofra-empty-state">{t("ofra.emptyState")}</div>}

      {isValidCalculation && (
        <div className="ofra-result-card">
          <strong className="results-title">{t("common.result")}</strong>
          <div className="ofra-result-value-row">
            <span className="ofra-result-label">{resultLabel}</span>
            <span className="ofra-result-value">{formatOfraNumber(calculation.calculatedValue)}</span>
          </div>
          <p className="ofra-result-note">{t("ofra.resultNote")}</p>
        </div>
      )}
    </section>
  );
}

export default OfraFertilizerRateCalculator;
